import { themeAtom } from "../../../shared/atoms";
import { log, logV } from "../../../shared/services/debug-logger";
import {
  ADMIN_UWHC_PROVIDER_FIELDS,
  ADMIN_UWHC_SERVICE_FIELDS,
  ADMIN_UWHC_TEXT_AREA_FIELDS,
  ADMIN_UWHC_TEXT_FIELDS,
  AdminUwhcProviderField,
  AdminUwhcProviderPayload,
  AdminUwhcProviderRow,
  getAdminUwhcProviderColumnLabel,
} from "../../models/admin-uwhc.models";
import {
  createEmptyAdminUwhcProviderPayload,
  getAdminUwhcProviderFieldOptions,
  getProviderDisplayName,
  normalizeProviderValue,
} from "../../utils/admin-uwhc.utils";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

type ModalMode = "create" | "edit";

interface Props {
  mode: ModalMode;
  row: AdminUwhcProviderRow | null;
  rows: AdminUwhcProviderRow[];
  saving: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onSave: (payload: AdminUwhcProviderPayload) => void | Promise<void>;
}

/**
 * Full-row editor mirrors the Streamlit grouped form while keeping persistence in the admin provider service.
 */
const AdminUwhcProviderModal = ({ mode, row, rows, saving, errorMessage, onCancel, onSave }: Props) => {
  const theme = useAtomValue(themeAtom);
  const [formValues, setFormValues] = useState<AdminUwhcProviderPayload>(() => createEmptyAdminUwhcProviderPayload());

  logV("AdminUwhcProviderModal - BUILD", { mode, saving });

  useEffect(() => {
    const nextValues = createEmptyAdminUwhcProviderPayload();
    ADMIN_UWHC_PROVIDER_FIELDS.forEach((field) => {
      nextValues[field] = normalizeProviderValue(row?.[field]);
    });
    log("STATE - applyProviderModalValues", { mode });
    setFormValues(nextValues);
  }, [mode, row]);

  const title = mode === "create" ? "Add UWHC Provider" : "Edit UWHC Provider";
  const subtitle = mode === "create" ? "New provider row" : getProviderDisplayName(row);

  const updateField = (field: AdminUwhcProviderField, value: string) => {
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    log("AdminUwhcProviderModal - onSubmit", { mode });

    const payload = ADMIN_UWHC_PROVIDER_FIELDS.reduce<AdminUwhcProviderPayload>((provider, field) => {
      provider[field] = normalizeProviderValue(formValues[field]);
      return provider;
    }, {});

    void Promise.resolve(onSave(payload));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-uwhc-provider-modal-title"
      data-test="admin-uwhc-provider-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-3 py-4"
    >
      <section
        className={twMerge(
          "flex max-h-[88vh] w-full max-w-6xl flex-col bg-white shadow-2xl dark:bg-gray-800",
          theme.home.tailwind.textDefault,
        )}
      >
        <ModalHeader title={title} subtitle={subtitle} row={row} saving={saving} onCancel={onCancel} />

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col" data-test="admin-uwhc-provider-form">
          <div
            className={twMerge(
              "min-h-0 flex-1 overflow-y-auto px-5 py-4",
              "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:w-2.5",
              "[&::-webkit-scrollbar-thumb]:bg-[#B7C9C4] [&::-webkit-scrollbar-track]:bg-[#F3F4F4]",
            )}
          >
            <TextFieldColumns rows={rows} values={formValues} onChange={updateField} />
            <CapabilitySection rows={rows} values={formValues} onChange={updateField} />
            <NotesSection values={formValues} onChange={updateField} />
          </div>

          {errorMessage && (
            <div
              role="alert"
              data-test="admin-uwhc-provider-modal-error"
              className="border-t border-[#F59692] bg-[#FFEAED] px-5 py-3 text-sm text-[#BF0002]"
            >
              {errorMessage}
            </div>
          )}

          <ModalActions mode={mode} saving={saving} onCancel={onCancel} />
        </form>
      </section>
    </div>
  );
};

// === MODAL PARTS ===

const ModalHeader = ({
  title,
  subtitle,
  row,
  saving,
  onCancel,
}: {
  title: string;
  subtitle: string;
  row: AdminUwhcProviderRow | null;
  saving: boolean;
  onCancel: () => void;
}) => (
  <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase text-green">Underwater Hull Cleaning</p>
      <h2 id="admin-uwhc-provider-modal-title" className="mt-0.5 truncate text-lg font-semibold">
        {title}
      </h2>
      <p className="mt-1 truncate text-xs opacity-70" data-test="admin-uwhc-provider-modal-subtitle">
        {row?.ID ? `ID ${row.ID} - ${subtitle}` : subtitle}
      </p>
    </div>
    <button
      type="button"
      aria-label="Close provider modal"
      title="Close"
      data-test="admin-uwhc-provider-modal-close"
      disabled={saving}
      className="flex h-9 w-9 flex-none items-center justify-center border border-gray-300 text-gray-500 hover:text-green disabled:opacity-50 dark:border-gray-700"
      onClick={onCancel}
    >
      <XMarkIcon className="h-5 w-5" />
    </button>
  </header>
);

const TextFieldColumns = ({
  rows,
  values,
  onChange,
}: {
  rows: AdminUwhcProviderRow[];
  values: AdminUwhcProviderPayload;
  onChange: (field: AdminUwhcProviderField, value: string) => void;
}) => {
  const midpoint = Math.ceil(ADMIN_UWHC_TEXT_FIELDS.length / 2);
  const columns = [ADMIN_UWHC_TEXT_FIELDS.slice(0, midpoint), ADMIN_UWHC_TEXT_FIELDS.slice(midpoint)];

  return (
    <section data-test="admin-uwhc-provider-modal-main-fields">
      <SectionTitle title="Provider Details" />
      <div className="grid gap-4 lg:grid-cols-2">
        {columns.map((fields, index) => (
          <div key={index} className="grid gap-3">
            {fields.map((field) => (
              <ProviderFieldInput
                key={field}
                field={field}
                rows={rows}
                value={values[field] ?? ""}
                onChange={onChange}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

const CapabilitySection = ({
  rows,
  values,
  onChange,
}: {
  rows: AdminUwhcProviderRow[];
  values: AdminUwhcProviderPayload;
  onChange: (field: AdminUwhcProviderField, value: string) => void;
}) => {
  const midpoint = Math.ceil(ADMIN_UWHC_SERVICE_FIELDS.length / 2);
  const columns = [ADMIN_UWHC_SERVICE_FIELDS.slice(0, midpoint), ADMIN_UWHC_SERVICE_FIELDS.slice(midpoint)];

  return (
    <section className="mt-6" data-test="admin-uwhc-provider-modal-capabilities">
      <SectionTitle title="Services And Capabilities" />
      <div className="grid gap-4 lg:grid-cols-2">
        {columns.map((fields, index) => (
          <div key={index} className="grid gap-2">
            {fields.map((field) => (
              <ProviderFieldInput
                key={field}
                field={field}
                rows={rows}
                value={values[field] ?? ""}
                onChange={onChange}
                compact
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

const NotesSection = ({
  values,
  onChange,
}: {
  values: AdminUwhcProviderPayload;
  onChange: (field: AdminUwhcProviderField, value: string) => void;
}) => (
  <section className="mt-6" data-test="admin-uwhc-provider-modal-notes">
    <SectionTitle title="Notes" />
    <div className="grid gap-4 lg:grid-cols-2">
      {ADMIN_UWHC_TEXT_AREA_FIELDS.map((field) => (
        <label key={field} className="grid gap-1.5 text-xs font-semibold uppercase">
          <span>{getAdminUwhcProviderColumnLabel(field)}</span>
          <textarea
            value={values[field] ?? ""}
            data-test={`admin-uwhc-provider-modal-field-${field}`}
            rows={10}
            className="min-h-[220px] resize-y border border-gray-300 bg-transparent px-3 py-2 text-sm font-normal normal-case leading-5 outline-none focus:border-green dark:border-gray-700"
            onChange={(event) => onChange(field, event.target.value)}
          />
        </label>
      ))}
    </div>
  </section>
);

const ProviderFieldInput = ({
  field,
  rows,
  value,
  compact,
  onChange,
}: {
  field: AdminUwhcProviderField;
  rows: AdminUwhcProviderRow[];
  value: string;
  compact?: boolean;
  onChange: (field: AdminUwhcProviderField, value: string) => void;
}) => {
  const options = useMemo(() => getAdminUwhcProviderFieldOptions(rows, field, value), [field, rows, value]);
  const listId = `admin-uwhc-provider-options-${field}`;

  return (
    <label className="grid gap-1 text-xs font-semibold uppercase">
      <span className="truncate">{getAdminUwhcProviderColumnLabel(field)}</span>
      <input
        value={value}
        list={listId}
        data-test={`admin-uwhc-provider-modal-field-${field}`}
        className={twMerge(
          "border border-gray-300 bg-transparent px-3 text-sm font-normal normal-case outline-none focus:border-green dark:border-gray-700",
          compact ? "h-8" : "h-9",
        )}
        onChange={(event) => onChange(field, event.target.value)}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </label>
  );
};

const SectionTitle = ({ title }: { title: string }) => (
  <h3 className="mb-3 border-b border-gray-200 pb-2 text-sm font-semibold dark:border-gray-700">{title}</h3>
);

const ModalActions = ({ mode, saving, onCancel }: { mode: ModalMode; saving: boolean; onCancel: () => void }) => (
  <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
    <button
      type="button"
      data-test="admin-uwhc-provider-modal-cancel"
      disabled={saving}
      onClick={onCancel}
      className="h-10 border border-gray-300 px-4 text-sm font-semibold disabled:opacity-50 dark:border-gray-700"
    >
      Cancel
    </button>
    <button
      type="submit"
      data-test="admin-uwhc-provider-modal-save"
      disabled={saving}
      className="inline-flex h-10 items-center gap-2 bg-green px-4 text-sm font-semibold text-white transition-colors hover:bg-[#166b60] disabled:cursor-not-allowed disabled:opacity-50"
    >
      <CheckIcon className="h-5 w-5" />
      {saving ? "Saving" : mode === "create" ? "Add Row" : "Save"}
    </button>
  </footer>
);

export default AdminUwhcProviderModal;
